import React, { ReactNode } from 'react';
import ValueChangeDisplay, {
	ValueChangeDisplayProps,
} from './ValueChangeDisplay';
import { cj, cm } from 'src/utils/classNames';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';

interface LabelAndValueProps
	extends WithChildren, Omit<DefaultProps, 'value'> {}

const LabelAndValue = ({
	label,
	warn,
	rightPadding,
	className,
	labelClassName,
	labelTooltip,
	children,
}: LabelAndValueProps) => {
	const labelContent = (() => {
		if (!labelTooltip) {
			return label;
		}

		return <Tooltip content={labelTooltip}>{label}</Tooltip>;
	})();

	return (
		<span
			className={cm(
				'flex w-full items-center justify-between typo-body-xs',
				className
			)}
		>
			<div
				className={cm(
					'shrink-0',
					warn ? 'text-star-yellow' : 'text-text-secondary',
					labelClassName
				)}
			>
				{labelContent}
			</div>

			<div className={cj('text-text-default', rightPadding)}>{children}</div>
		</span>
	);
};

interface ValueChangeProps
	extends ValueChangeDisplayProps, Pick<DefaultProps, 'label'> {
	nullOverride?: boolean;
	customOverride?: ReactNode;
}

const ValueChange = ({
	label,
	nullOverride,
	customOverride,
	...rest
}: ValueChangeProps) => {
	const content = (() => {
		if (nullOverride) {
			return customOverride ? customOverride : '-';
		}

		return <ValueChangeDisplay {...rest} />;
	})();

	return <LabelAndValue label={label}>{content}</LabelAndValue>;
};

interface DefaultProps extends WithClassName {
	label: ReactNode;
	value: ReactNode;
	warn?: boolean;
	rightPadding?: string;
	labelClassName?: string;
	valueClassName?: string;
	labelTooltip?: ReactNode;
}

const Default = ({
	label,
	value,
	warn,
	rightPadding,
	className,
	valueClassName,
	labelTooltip,
}: DefaultProps) => {
	return (
		<LabelAndValue
			label={label}
			warn={warn}
			rightPadding={rightPadding}
			className={className}
			labelTooltip={labelTooltip}
		>
			<div
				className={cm(
					warn ? 'text-star-yellow' : 'text-text-default',
					valueClassName
				)}
			>
				{value ?? '-'}
			</div>
		</LabelAndValue>
	);
};

const ValueDisplay = {
	ValueChange: React.memo(ValueChange),
	Default: React.memo(Default),
};

export default ValueDisplay;
